import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Terms = () => {
  // Set current date as effective date if not specified
  const effectiveDate = "May 14, 2025";

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 container mx-auto px-4 py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-6">Terms of Service</h1>
        <p className="text-gray-600 mb-8">Effective Date: {effectiveDate}</p>

        <p className="mb-8">
          Welcome to stickersby.ai, a service provided by Three Ten Labs. These Terms of Service
          (&ldquo;Terms&rdquo;) govern your use of our website and services. By accessing or using
          the site, you agree to be bound by these Terms. If you do not agree, please do not use our
          services.
        </p>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">1. Use of the Service</h2>
          <p className="mb-4">
            Stickersby.ai allows users to create and purchase custom AI-generated sticker designs.
            You agree to use the service in accordance with these Terms and all applicable laws.
          </p>

          <p className="mb-2">You may not:</p>
          <ul className="list-disc pl-8 space-y-1">
            <li>
              Use the service to generate or share content that is unlawful, obscene, harmful, or
              offensive
            </li>
            <li>Infringe on the intellectual property rights of others</li>
            <li>Attempt to reverse-engineer or interfere with the platform</li>
            <li>Use the platform to harm or harass others</li>
          </ul>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">2. Your Content & Prompts</h2>
          <p className="mb-4">
            You retain ownership of the prompts, concepts, and inputs you provide. By using the
            service, you grant us a limited license to use your inputs to generate content, deliver
            services, and operate the platform.
          </p>
          <p>
            You are solely responsible for the legality, originality, and appropriateness of your
            submitted content.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">3. AI-Generated Output</h2>
          <p className="mb-4">
            Sticker designs are created using third-party AI tools. While we aim to generate
            high-quality and appropriate images, we do not guarantee the accuracy, originality, or
            suitability of any generated content.
          </p>
          <p>
            You may use generated images for personal or commercial use, provided you have
            appropriate rights to do so. You understand that similar or overlapping designs may be
            produced for other users.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">4. Accounts and Access</h2>
          <p className="mb-4">
            Some features require you to register for an account. You agree to keep your login
            information secure and accept responsibility for all activity under your account.
          </p>
          <p>
            We may suspend or terminate your access if you violate these Terms or abuse the service.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">5. Purchases and Payments</h2>
          <p>
            All purchases are processed securely through third-party payment providers. Because the
            products are custom-generated, all sales are final, except in cases of technical failure
            or undeliverable output.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">6. Intellectual Property</h2>
          <p>
            All website content (excluding your generated designs) — including logos, UI elements,
            and branding — is owned by Three Ten Labs and protected under intellectual property
            laws. You may not copy, modify, or distribute any part of the platform without
            permission.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">7. Use of Customer Creations for Marketing</h2>
          <p className="mb-4">
            By using Stickersby.ai, you grant us a non-exclusive, royalty-free, worldwide license to
            use your generated sticker designs for marketing and promotional purposes only. This
            includes showcasing customer creations in:
          </p>

          <ul className="list-disc pl-8 mb-4 space-y-1">
            <li>Public examples or galleries on our website</li>
            <li>Social media, ads, and newsletters</li>
            <li>Press kits or platform promotions</li>
          </ul>

          <p className="mb-4">
            We will never sell or license your designs to third parties, and they will only be used
            to promote Stickersby.ai.
          </p>

          <p>
            If you would prefer your designs not be used in this way, you may opt out by contacting
            us at{" "}
            <a href="mailto:support@threetenlabs.com" className="text-primary hover:underline">
              support@threetenlabs.com
            </a>
            .
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">8. Third-Party Services</h2>
          <p>
            Our service may rely on third-party tools (such as AI APIs, analytics, or payment
            processors). Your use of those services is subject to their respective terms and privacy
            practices.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">9. Termination</h2>
          <p>
            We reserve the right to suspend or terminate your access if you violate these Terms or
            misuse the platform. You may also request account deletion at any time by contacting us.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">10. Disclaimers</h2>
          <p className="mb-4">
            Stickersby.ai is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; We make no
            guarantees about uptime, image quality, prompt accuracy, or content outcomes. To the
            fullest extent permitted by law, we disclaim all warranties, express or implied.
          </p>
          <p>
            We are not liable for indirect, incidental, or consequential damages resulting from your
            use of the service.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">11. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. If we make significant changes, we will
            post a notice on the site. Your continued use of the service after changes means you
            accept the revised Terms.
          </p>
        </section>

        <hr className="border-gray-300 my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">12. Contact Us</h2>
          <p className="mb-2">Have questions?</p>
          <p>
            📧{" "}
            <a href="mailto:support@threetenlabs.com" className="text-primary hover:underline">
              support@threetenlabs.com
            </a>
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Terms;
